import { useMemo } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Package, FileText, TrendingUp, Clock, CheckCircle, Truck, Building2, DollarSign, PieChart, ArrowUpRight, MapPin, Phone, User as UserIcon, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useShipments } from '@/hooks/useShipments';
import { useInvoices } from '@/hooks/useInvoices';
import { useCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useWarehouses';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

export default function CustomerDashboard() {
  const { user } = useUser();
  const { t } = useLanguage();

  const { shipments: apiShipments } = useShipments();
  const { invoices: apiInvoices } = useInvoices();
  const { customers: apiCustomers } = useCustomers();
  const { warehouses: apiWarehouses } = useWarehouses();

  const shipments = (apiShipments || []).filter((s: any) =>
    (s.customer_code || s.customerCode) === user?.customerCode
  );
  
  const invoices = (apiInvoices || [])
    .filter((i: any) => (i.customer_code || i.customerCode) === user?.customerCode)
    .map((i: any) => ({
      ...i,
      total: parseFloat(i.total) || 0,
      amountPaid: parseFloat(i.amount_paid || i.amountPaid) || 0,
      balance: parseFloat(i.balance_due || i.balance) || 0,
      subtotal: parseFloat(i.subtotal) || 0,
      discount: parseFloat(i.discount) || 0,
      tax: parseFloat(i.tax) || 0,
    }));
    
  const customerData = (apiCustomers || []).find((c: any) => c.code === user?.customerCode);

  // Data now loaded from API hooks above - no useEffect needed

  // Calculate statistics
  const stats = useMemo(() => {
    const totalShipments = shipments.length;
    const processingShipments = shipments.filter(s => s.status === 'Processing').length;
    const inTransitShipments = shipments.filter(s => s.status === 'In Transit').length;
    const deliveredShipments = shipments.filter(s => s.status === 'Delivered').length;

    const totalInvoices = invoices.length;
    const paidInvoices = invoices.filter(i => i.status === 'Paid').length;
    const unpaidInvoices = invoices.filter(i => i.status !== 'Paid').length;
    const totalAmount = invoices.reduce((sum, i) => sum + (i.total || 0), 0);
    const totalPaid = invoices.reduce((sum, i) => sum + (i.amountPaid || 0), 0);
    const totalBalance = invoices.reduce((sum, i) => sum + (i.balance || 0), 0);

    return {
      totalShipments,
      processingShipments,
      inTransitShipments,
      deliveredShipments,
      totalInvoices,
      paidInvoices,
      unpaidInvoices,
      totalAmount,
      totalPaid,
      totalBalance
    };
  }, [shipments, invoices]);

  const getStatusTranslation = (status: string): string => {
    const map: Record<string, string> = {
      'Processing': t('processing'),
      'In Transit': t('inTransit'),
      'At Destination': t('atDestination'),
      'Delivered': t('delivered'),
      'Planning': t('planning'),
      'Completed': t('completed'),
      'Draft': t('draft'),
      'Sent': t('sent'),
      'Paid': t('paid'),
      'Overdue': t('overdue'),
      'Partial': t('partial'),
      'Active': t('active'),
      'Inactive': t('inactive'),
    };
    return map[status] || status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered': return 'bg-green-100 text-green-800';
      case 'In Transit': return 'bg-blue-100 text-blue-800';
      case 'At Destination': return 'bg-purple-100 text-purple-800';
      case 'Processing': return 'bg-yellow-100 text-yellow-800';
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Sent': return 'bg-blue-100 text-blue-800';
      case 'Draft': return 'bg-gray-100 text-gray-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      case 'Partial': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">
            {t('welcome')}, {customerData?.name || user?.customerName || t('customerFallback')}!
          </h1>
          <p className="text-blue-100">{t('customerCode')} {user?.customerCode}</p>
        </div>

        {/* Statistics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Shipments */}
          <Card className="border-t-4 border-t-blue-500 hover:shadow-lg transition-all duration-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('totalShipments')}</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalShipments}</p>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <ArrowUpRight className="h-3 w-3 text-blue-600" />
                    {t('yourShipments')}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* In Transit */}
          <Card className="border-t-4 border-t-green-500 hover:shadow-lg transition-all duration-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('inTransit')}</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.inTransitShipments}</p>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Truck className="h-3 w-3 text-green-600" />
                    {t('onTheWay')}
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Truck className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Invoices */}
          <Card className="border-t-4 border-t-purple-500 hover:shadow-lg transition-all duration-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('totalInvoices')}</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalInvoices}</p>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <FileText className="h-3 w-3 text-purple-600" />
                    {t('yourInvoices')}
                  </p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Outstanding Balance */}
          <Card className="border-t-4 border-t-orange-500 hover:shadow-lg transition-all duration-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('outstanding')}</p>
                  <p className="text-3xl font-bold text-gray-900">${stats.totalBalance.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <DollarSign className="h-3 w-3 text-orange-600" />
                    {t('amountDue')}
                  </p>
                </div>
                <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Warehouse Information */}
        <Card className="border-t-4 border-t-orange-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-600" />
              {t('ourWarehouseLocations')}
            </CardTitle>
            <CardDescription>{t('contactInfoForWarehouses')}</CardDescription>
          </CardHeader>
          <CardContent>
            {apiWarehouses && apiWarehouses.length > 0 ? (
              <div className="space-y-4">
                {/* Main Warehouse - Featured */}
                {(() => {
                  const mainWarehouse = apiWarehouses.find((w: any) => w.type === 'Main') || apiWarehouses[0];
                  return (
                    <div className="bg-gradient-to-r from-[#1e3a6e] to-[#2d4f8f] text-white rounded-lg p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold mb-1">{mainWarehouse.name}</h3>
                          <Badge className="bg-[#f5a623] text-white border-0">{t('mainWarehouse')}</Badge>
                        </div>
                        <Building2 className="h-8 w-8 text-white/80" />
                      </div>
                      
                      <div className="space-y-3 text-blue-50">
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5 text-[#f5a623]" />
                          <div>
                            <p className="font-medium text-white">{t('address')}</p>
                            <p className="text-sm">{mainWarehouse.address}</p>
                            <p className="text-sm">{mainWarehouse.city}, {mainWarehouse.state}, {mainWarehouse.country}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <UserIcon className="h-5 w-5 flex-shrink-0 text-[#f5a623]" />
                          <div>
                            <p className="font-medium text-white">{t('warehouseManager')}</p>
                            <p className="text-sm">{mainWarehouse.manager || t('notAssigned')}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Phone className="h-5 w-5 flex-shrink-0 text-[#f5a623]" />
                          <div>
                            <p className="font-medium text-white">{t('phone')}</p>
                            <p className="text-sm">{mainWarehouse.phone}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}


              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>{t('noWarehouseInfoAvailable')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shipment Status Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-blue-600" />
              {t('shipmentStatus')}
            </CardTitle>
            <CardDescription>{t('visualBreakdownShipments')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPie>
                <Pie
                  data={[
                    { name: 'Processing', value: stats.processingShipments, color: '#eab308' },
                    { name: 'In Transit', value: stats.inTransitShipments, color: '#3b82f6' },
                    { name: 'At Destination', value: shipments.filter(s => s.status === 'At Destination').length, color: '#8b5cf6' },
                    { name: 'Delivered', value: stats.deliveredShipments, color: '#22c55e' },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[
                    { name: 'Processing', value: stats.processingShipments, color: '#eab308' },
                    { name: 'In Transit', value: stats.inTransitShipments, color: '#3b82f6' },
                    { name: 'At Destination', value: shipments.filter(s => s.status === 'At Destination').length, color: '#8b5cf6' },
                    { name: 'Delivered', value: stats.deliveredShipments, color: '#22c55e' },
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Shipment Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>{t('shipmentStatus')} {t('details')}</CardTitle>
            <CardDescription>{t('currentStatusOfShipments')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">{t('processing')}</span>
                </div>
                <p className="text-2xl font-bold text-yellow-900 mt-2">{stats.processingShipments}</p>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center space-x-2">
                  <Truck className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">{t('inTransit')}</span>
                </div>
                <p className="text-2xl font-bold text-blue-900 mt-2">{stats.inTransitShipments}</p>
              </div>

              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">{t('atDestination')}</span>
                </div>
                <p className="text-2xl font-bold text-purple-900 mt-2">
                  {shipments.filter(s => s.status === 'At Destination').length}
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">{t('delivered')}</span>
                </div>
                <p className="text-2xl font-bold text-green-900 mt-2">{stats.deliveredShipments}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Shipments & Invoices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Shipments */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('recentShipments')}</CardTitle>
                <Link to="/shipments">
                  <Button variant="outline" size="sm">{t('viewAll')}</Button>
                </Link>
              </div>
              <CardDescription>{t('latestShipmentActivity')}</CardDescription>
            </CardHeader>
            <CardContent>
              {shipments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>{t('noShipmentsYet')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {shipments.slice(0, 5).map((shipment) => (
                    <div key={shipment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{shipment.shipmentNumber}</p>
                        <p className="text-xs text-gray-500">{shipment.description || t('notAvailable')}</p>
                      </div>
                      <Badge className={`${getStatusColor(shipment.status)} text-xs`}>
                        {getStatusTranslation(shipment.status)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Invoices */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('recentInvoices')}</CardTitle>
                <Link to="/invoices">
                  <Button variant="outline" size="sm">{t('viewAll')}</Button>
                </Link>
              </div>
              <CardDescription>{t('latestInvoiceActivity')}</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p>{t('noInvoicesYet')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.slice(0, 5).map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{invoice.invoice_number || invoice.invoiceNumber}</p>
                        <p className="text-xs text-gray-500">{t('amount')}: ${invoice.total.toFixed(2)}</p>
                      </div>
                      <Badge className={`${getStatusColor(invoice.status)} text-xs`}>
                        {getStatusTranslation(invoice.status)}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>{t('quickActions')}</CardTitle>
            <CardDescription>{t('commonTasksAndShortcuts')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link to="/shipments">
                <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center space-y-2">
                  <Package className="h-6 w-6" />
                  <span>{t('viewMyShipments')}</span>
                </Button>
              </Link>

              <Link to="/invoices">
                <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center space-y-2">
                  <FileText className="h-6 w-6" />
                  <span>{t('viewMyInvoices')}</span>
                </Button>
              </Link>

              <Link to="/profile">
                <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center space-y-2">
                  <TrendingUp className="h-6 w-6" />
                  <span>{t('myProfile')}</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
